import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
  type CSSProperties, type KeyboardEvent as ReactKeyboardEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { Check, Lock } from 'lucide-react'
import type { CardSenseCandidate } from '../../hooks/useFill'
import { useLocale } from '../../i18n/context'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import {
  glossOf, liftIntoViewport, placeMeaningPopover, MEANING_LIST_MAX_HEIGHT,
  type MeaningPlacement, type Rect,
} from '../../utils/meaningChooser'

/**
 * The meaning chooser (DESIGN_SYSTEM.md §14) — the card's linked sense, stated
 * in words in the Definition label band, and every alternative listed by name
 * when opened.
 *
 * It replaces a chevron pair with an "N of M" counter. That idiom now belongs
 * to the card navigator alone, and nothing here may borrow it in any state:
 * no stepping, no position, no count shaped like a position. The count that
 * does appear is prose in the list's heading.
 *
 * NOTHING THIS RENDERS IS IN FLOW beyond the 22px capsule, which sits inside a
 * band whose height is fixed by its own row style. The open list is portalled
 * to `document.body` and fixed-positioned. Both halves are load-bearing:
 * the card's height is what ADR-067 Decision A positions a focused field
 * against, so a list that pushed the fields down would move the caret out from
 * under the reader; and the phone's card wrapper carries a CSS `filter`, which
 * makes it the containing block for any `position: fixed` descendant — a
 * non-portalled list would be pinned to the card, not the viewport.
 *
 * React events still bubble through the portal to the card, so every key the
 * list handles is stopped here: the card's own Escape (end the edit session)
 * and Enter handlers must not act on a press the list already consumed.
 */
export interface MeaningChooserProps {
  term: string
  candidates: CardSenseCandidate[]
  linkedId: string | null
  /** The card's definition is authored (ADR-063 amendment, 2026-09-11): the
   *  list still opens, still shows every sense and the mark, and refuses. */
  locked: boolean
  /** Whether the card already carries a Translation of its own — decides what
   *  the footer can truthfully promise about the translation. */
  translationOnCard: boolean
  onChoose: (candidate: CardSenseCandidate) => void
}

export default function MeaningChooser({
  term, candidates, linkedId, locked, translationOnCard, onChoose,
}: MeaningChooserProps) {
  const { tr } = useLocale()
  const cb = tr.cardBubble
  const [open, setOpen] = useState(false)
  // ADR-106 P3: the open list is a popover over the card; blocks while open.
  useSwipeNavBlock(open, 'meaning-chooser')
  const [placement, setPlacement] = useState<MeaningPlacement | null>(null)
  const capsuleRef = useRef<HTMLButtonElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([])

  const linked = candidates.find((c) => c.word_entry_id === linkedId) ?? null
  const linkedGloss = linked ? (glossOf(linked.definition) || cb.meaningNoDefinition) : null

  const measure = useCallback(() => {
    const capsule = capsuleRef.current
    if (!capsule) return
    // The band, not the capsule: `below` spans the band's width so the list
    // lines up with the fields under it rather than with a truncated pill.
    const band = (capsule.closest('[data-meaning-band]') as HTMLElement | null) ?? capsule
    setPlacement(placeMeaningPopover(
      toRect(band.getBoundingClientRect()),
      scrollerRectOf(band),
      { width: window.innerWidth, height: window.innerHeight },
    ))
  }, [])

  useLayoutEffect(() => {
    if (open) measure()
  }, [open, measure])

  // Second pass, once the list has its natural height: raise it by whatever
  // would fall off the bottom of the viewport. Before paint, so the reader
  // never sees it jump; converges in one step because the lifted top is a
  // fixed point of `liftIntoViewport`.
  useLayoutEffect(() => {
    const el = popoverRef.current
    if (!el || !placement) return
    const top = liftIntoViewport(placement.top, el.getBoundingClientRect().height, window.innerHeight)
    if (top !== placement.top) setPlacement({ ...placement, top })
  }, [placement])

  // Follow the band while open: the pane or the feed may scroll under it (a
  // wheel over the pane, the phone's visual viewport settling). Capture phase,
  // so a scroll in ANY ancestor is seen; the list's own scroll is ignored.
  useEffect(() => {
    if (!open) return
    let frame = 0
    const follow = (e: Event) => {
      if (popoverRef.current && e.target instanceof Node && popoverRef.current.contains(e.target)) return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(measure)
    }
    window.addEventListener('scroll', follow, true)
    window.addEventListener('resize', follow)
    window.visualViewport?.addEventListener('resize', follow)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('scroll', follow, true)
      window.removeEventListener('resize', follow)
      window.visualViewport?.removeEventListener('resize', follow)
    }
  }, [open, measure])

  // Dismiss on a press anywhere else. Pointerdown rather than click so the
  // press that lands on a field closes the list before the field takes focus.
  useEffect(() => {
    if (!open) return
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node | null
      if (!t) return
      if (popoverRef.current?.contains(t) || capsuleRef.current?.contains(t)) return
      setOpen(false)
      setPlacement(null)
    }
    document.addEventListener('pointerdown', onDown, true)
    return () => document.removeEventListener('pointerdown', onDown, true)
  }, [open])

  // Focus the linked sense (or the first) once the list is placed, so the
  // keyboard lands inside it; the capsule gets focus back on Escape.
  useEffect(() => {
    if (!open || !placement) return
    const idx = Math.max(0, candidates.findIndex((c) => c.word_entry_id === linkedId))
    optionRefs.current[idx]?.focus({ preventScroll: false })
    // Only on opening — re-focusing on every re-placement would yank focus
    // back while the reader arrows through the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, placement !== null])

  function close(returnFocus: boolean) {
    setOpen(false)
    setPlacement(null)
    if (returnFocus) capsuleRef.current?.focus()
  }

  function choose(c: CardSenseCandidate) {
    if (locked) return
    close(false)
    if (c.word_entry_id !== linkedId) onChoose(c)
  }

  function handleListKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    // Every key this list acts on is claimed twice over: `preventDefault` for
    // the deck screen's window-level Escape/Enter ladders, which check
    // `defaultPrevented`; `stopPropagation` for the card's own onKeyDown,
    // which the portal would otherwise still bubble to.
    const opts = optionRefs.current.filter((el): el is HTMLButtonElement => !!el)
    const at = opts.findIndex((el) => el === document.activeElement)
    const move = (i: number) => opts[(i + opts.length) % opts.length]?.focus()
    switch (e.key) {
      case 'Escape': e.preventDefault(); e.stopPropagation(); close(true); return
      case 'ArrowDown': e.preventDefault(); e.stopPropagation(); move(at + 1); return
      case 'ArrowUp': e.preventDefault(); e.stopPropagation(); move(at - 1); return
      case 'Home': e.preventDefault(); e.stopPropagation(); move(0); return
      case 'End': e.preventDefault(); e.stopPropagation(); move(opts.length - 1); return
      case 'Tab': e.preventDefault(); e.stopPropagation(); close(true); return
      case 'Enter': case ' ':
        // The option's own click does the choosing; this only keeps the press
        // from reaching the card and the screen's Enter ladder as well.
        e.stopPropagation()
        return
    }
  }

  const count = candidates.length
  const popover = open && placement ? createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={cb.meaningListHeading(term)}
      onKeyDown={handleListKeyDown}
      // Presses inside must not read as presses on the card or the screen
      // behind it (the desktop shell's background-click deselects).
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={popoverStyle(placement)}
    >
      <div style={headStyle}>
        <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{cb.meaningListHeading(term)}</span>
        {` · ${cb.meaningListCount(count)}`}
        {!locked && ` · ${cb.meaningListInstruction}`}
      </div>
      <div
        role="listbox"
        aria-label={cb.meaningListHeading(term)}
        aria-disabled={locked || undefined}
        style={listStyle}
      >
        {candidates.map((c, i) => {
          const isLinked = c.word_entry_id === linkedId
          const gloss = glossOf(c.definition)
          return (
            <button
              key={c.word_entry_id}
              ref={(el) => { optionRefs.current[i] = el }}
              role="option"
              aria-selected={isLinked}
              aria-disabled={locked || undefined}
              onClick={() => choose(c)}
              style={optionStyle(isLinked, locked)}
            >
              {/* The mark's column is reserved on every row, so glosses start on
                  one vertical line and the mark reads as a mark, not an indent. */}
              <span style={markColumnStyle} aria-hidden="true">
                {isLinked && <Check size={13} strokeWidth={2.6} />}
              </span>
              <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, flex: 1 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  {c.pos && <span style={posTagStyle}>{c.pos}</span>}
                  {isLinked && <span style={linkedTagStyle}>{cb.meaningLinked}</span>}
                </span>
                <span style={glossStyle(!gloss)}>{gloss || cb.meaningNoDefinition}</span>
              </span>
            </button>
          )
        })}
      </div>
      {locked ? (
        <div role="note" style={{ ...footStyle, color: 'var(--text-primary)', display: 'flex', gap: 8 }}>
          <Lock size={13} strokeWidth={2.2} style={{ flexShrink: 0, marginTop: 2, color: 'var(--text-secondary)' }} />
          <span>{cb.meaningLocked}</span>
        </div>
      ) : (
        <div style={footStyle}>{cb.meaningFooter(translationOnCard)}</div>
      )}
    </div>,
    document.body,
  ) : null

  return (
    <>
      <button
        ref={capsuleRef}
        type="button"
        data-meaning-capsule=""
        onClick={(e) => { e.stopPropagation(); if (open) close(false); else { setPlacement(null); setOpen(true) } }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={cb.meaningCapsuleAria(linkedGloss, locked)}
        // Reachable, but not a station on the field-to-field walk (brief §22),
        // the same rule the handle and the trash in this band follow.
        tabIndex={-1}
        style={capsuleStyle(open)}
      >
        {locked && <Lock size={10} strokeWidth={2.4} style={{ flexShrink: 0 }} aria-hidden="true" />}
        <span style={capsuleEyebrowStyle}>{cb.meaningLabel}</span>
        {linked?.pos && <span style={capsulePosStyle}>{linked.pos}</span>}
        <span style={capsuleGlossStyle(!linked)}>{linkedGloss ?? cb.meaningNotLinked}</span>
      </button>
      {popover}
    </>
  )
}

/** The band's statement that no dictionary entry stands behind the card
 *  (§14 item 6). Same 22px geometry as the capsule, and no control. */
export function NoDictionaryLinkTag() {
  const { tr } = useLocale()
  return <span data-meaning-capsule="" style={noLinkTagStyle}>{tr.cardBubble.meaningNoDictionaryLink}</span>
}

function toRect(r: DOMRect): Rect {
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom, width: r.width }
}

/** The nearest ancestor that scrolls vertically — the desktop pane, the
 *  narrow-desktop sheet, or the phone's feed. Its left edge is what `beside`
 *  placement is measured from. */
function scrollerRectOf(el: HTMLElement): Rect | null {
  let node = el.parentElement
  while (node && node !== document.body) {
    const oy = getComputedStyle(node).overflowY
    if (oy === 'auto' || oy === 'scroll') return toRect(node.getBoundingClientRect())
    node = node.parentElement
  }
  return null
}

// ── Styles ─────────────────────────────────────────────────────────────────

// 22px inside the band's fixed 24px row: the capsule never sets the band's
// height, whichever state it is in.
const CAPSULE_H = 22

function capsuleStyle(open: boolean): CSSProperties {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    height: CAPSULE_H, boxSizing: 'border-box',
    flex: '0 1 auto', minWidth: 0, maxWidth: '100%',
    padding: '0 10px', margin: 0,
    borderRadius: 'var(--radius-pill)',
    border: `1px solid ${open ? 'var(--accent)' : 'var(--border)'}`,
    background: open ? 'var(--accent-soft)' : 'var(--bg-page)',
    color: 'var(--text-secondary)',
    cursor: 'pointer', overflow: 'hidden',
  }
}

const capsuleEyebrowStyle: CSSProperties = {
  flexShrink: 0,
  fontFamily: 'var(--font-ui)', fontSize: 8.5, fontWeight: 700, letterSpacing: '0.09em',
  textTransform: 'uppercase', color: 'var(--text-secondary)',
}

const capsulePosStyle: CSSProperties = {
  flexShrink: 0,
  fontFamily: 'var(--font-ui)', fontSize: 10, fontStyle: 'italic', color: 'var(--text-secondary)',
}

function capsuleGlossStyle(placeholder: boolean): CSSProperties {
  return {
    minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontFamily: 'var(--font-ui)', fontSize: 11.5, lineHeight: `${CAPSULE_H - 2}px`,
    color: placeholder ? 'var(--text-tertiary)' : 'var(--text-primary)',
    fontStyle: placeholder ? 'italic' : 'normal',
  }
}

const noLinkTagStyle: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', flexShrink: 0,
  height: CAPSULE_H, boxSizing: 'border-box', padding: '0 10px',
  borderRadius: 'var(--radius-pill)', border: '1px dashed var(--border)',
  fontFamily: 'var(--font-ui)', fontSize: 10.5, color: 'var(--text-tertiary)',
  whiteSpace: 'nowrap',
}

function popoverStyle(p: MeaningPlacement): CSSProperties {
  const base: CSSProperties = {
    position: 'fixed', zIndex: 280, left: p.left, width: p.width, maxHeight: p.maxHeight,
    boxSizing: 'border-box', display: 'flex', flexDirection: 'column',
    background: 'var(--bg-surface)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-float)',
    overflow: 'hidden',
  }
  return { ...base, top: p.top }
}

const headStyle: CSSProperties = {
  flexShrink: 0, padding: '10px 12px 8px',
  borderBottom: '1px solid var(--border)',
  fontFamily: 'var(--font-ui)', fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)',
}

// §14: the LIST caps and scrolls; the head and the foot stay in view. `minHeight:
// 0` lets it also yield to a short viewport before the foot is pushed out.
const listStyle: CSSProperties = {
  flex: '0 1 auto', minHeight: 0, maxHeight: MEANING_LIST_MAX_HEIGHT,
  overflowY: 'auto', overscrollBehavior: 'contain', padding: 4,
}

function optionStyle(linked: boolean, locked: boolean): CSSProperties {
  return {
    display: 'flex', alignItems: 'flex-start', gap: 6, width: '100%',
    textAlign: 'left', padding: '8px 8px 8px 4px', margin: 0,
    border: 'none', borderRadius: 'var(--radius-sm)',
    background: linked ? 'var(--accent-soft)' : 'transparent',
    color: 'var(--text-primary)',
    // Refused rows stay at full legibility (§14 item 5) — only the pointer says
    // a press will not act; the overlay's own note says why.
    cursor: locked ? 'default' : 'pointer',
  }
}

const markColumnStyle: CSSProperties = {
  width: 16, flexShrink: 0, height: 16, marginTop: 1,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--accent)',
}

const posTagStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 9, fontWeight: 600, letterSpacing: '0.06em',
  textTransform: 'uppercase', color: 'var(--text-secondary)', background: 'var(--bg-page)',
  border: '1px solid var(--border)', padding: '1px 6px', borderRadius: 'var(--radius-pill)',
}

const linkedTagStyle: CSSProperties = {
  fontFamily: 'var(--font-ui)', fontSize: 9, fontWeight: 700, letterSpacing: '0.07em',
  textTransform: 'uppercase', color: 'var(--accent)',
}

// One or two lines (§14): the clause cut in `glossOf` does the trimming, the
// clamp only bounds a definition that has no clause to cut at.
function glossStyle(missing: boolean): CSSProperties {
  return {
    fontFamily: 'var(--font-content)', fontSize: 13.5, lineHeight: 1.4,
    color: missing ? 'var(--text-tertiary)' : 'var(--text-primary)',
    fontStyle: missing ? 'italic' : 'normal',
    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
  }
}

const footStyle: CSSProperties = {
  flexShrink: 0, padding: '8px 12px 10px',
  borderTop: '1px solid var(--border)',
  fontFamily: 'var(--font-ui)', fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)',
}
